// frontend/src/contexts/StaffAreaContext.tsx
import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';

/**
 * Which staff shell a page is rendered in. The catalogue record pages (authors,
 * publishers, genres) are shared by the librarian app and the admin panel; they
 * build every link from `base` and set the title through whichever topbar the
 * shell owns, so an admin never lands in `/librarian/...` and vice versa.
 */
export type StaffBase = '/librarian' | '/admin';

interface TopbarSetters {
  setTitle: (t: string) => void;
  setSubtitle: (s: string | null) => void;
}

interface StaffArea {
  base: StaffBase;
  topbar: TopbarSetters | null;
}

// Outside a shell (unit tests) links default to the librarian app and the
// topbar is a no-op.
const StaffAreaCtx = createContext<StaffArea>({ base: '/librarian', topbar: null });

export const StaffAreaProvider: React.FC<{
  base: StaffBase;
  topbar: TopbarSetters;
  children: ReactNode;
}> = ({ base, topbar, children }) => {
  const { setTitle, setSubtitle } = topbar;
  const value = useMemo(() => ({ base, topbar: { setTitle, setSubtitle } }), [base, setTitle, setSubtitle]);
  return <StaffAreaCtx.Provider value={value}>{children}</StaffAreaCtx.Provider>;
};

/** Sets the shell's topbar title/subtitle, whichever shell this is. */
export const useStaffTopbar = (next: { title?: string; subtitle?: string | null }) => {
  const { topbar } = useContext(StaffAreaCtx);
  useEffect(() => {
    if (!topbar) return;
    if (next.title !== undefined) topbar.setTitle(next.title);
    if (next.subtitle !== undefined) topbar.setSubtitle(next.subtitle);
  }, [topbar, next.title, next.subtitle]);
};

const query = (params?: Record<string, string | number | undefined>) => {
  const entries = Object.entries(params ?? {}).filter(([, v]) => v !== undefined) as [string, string | number][];
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : '';
};

/** Links for the shared catalogue pages, rooted in the current shell. */
export const useStaffPaths = () => {
  const { base } = useContext(StaffAreaCtx);
  return useMemo(
    () => ({
      base,
      authors: (id?: number) => (id ? `${base}/authors/${id}` : `${base}/authors`),
      authorNew: () => `${base}/authors/new`,
      authorEdit: (id: number) => `${base}/authors/${id}/edit`,
      publishers: (id?: number) => (id ? `${base}/publishers/${id}` : `${base}/publishers`),
      publisherNew: () => `${base}/publishers/new`,
      publisherEdit: (id: number) => `${base}/publishers/${id}/edit`,
      genres: (id?: number) => (id ? `${base}/genres/${id}` : `${base}/genres`),
      /** New-genre form, optionally started under a parent: `{ parent }` ("Add sub-genre"). */
      genreNew: (preset?: { parent?: number }) => `${base}/genres/new${query(preset)}`,
      genreEdit: (id: number) => `${base}/genres/${id}/edit`,
      locations: (id?: number) => (id ? `${base}/locations/${id}` : `${base}/locations`),
      locationNew: () => `${base}/locations/new`,
      locationEdit: (id: number) => `${base}/locations/${id}/edit`,
      books: (id?: number) => (id ? `${base}/books/${id}` : `${base}/books`),
      bookEdit: (id: number) => `${base}/books/${id}/edit`,
      /** The Loans page: narrowed to one book's copies (`{ book }`) or opened on a filter chip
       *  (`{ filter: 'overdue' | 'due-today' | 'returned-today' | 'requests' | 'active' }`). */
      loans: (preset?: { book?: number; filter?: string }) => `${base}/loans${query(preset)}`,
      /** The issue form and a loan's own page: the same pages in both shells. */
      loanNew: () => `${base}/loans/new`,
      loan: (id: number) => `${base}/loans/${id}`,
      /** Accounts live under Users in the admin panel and Members in the librarian app. */
      members: () => (base === '/admin' ? '/admin/users' : '/librarian/members'),
      memberNew: () => (base === '/admin' ? '/admin/users/new' : '/librarian/members/new'),
      /** New-book form with a record preselected: `{ author }`, `{ publisher }` or `{ genre }`. */
      bookNew: (preset?: { author?: number; publisher?: number; genre?: number }) =>
        `${base}/books/new${query(preset)}`,
    }),
    [base],
  );
};
